import React, { useState, useEffect, useRef } from 'react';
import QuestionnaireForm from './components/QuestionnaireForm';
import FollowUpAccessGate from './components/FollowUpAccessGate';
import ConfirmDetails from './components/ConfirmDetails';
import { useAuth } from './hooks/useAuth';
import { useVisitorTracking } from './hooks/useVisitorTracking';
import LoadingSpinner from './components/LoadingSpinner';
import CheckoutPage from './components/CheckoutPage';
import TreatmentCheckoutPage, { treatmentCheckoutStorageKey } from './features/treatment-flow/TreatmentCheckoutPage';
import LabCheckoutPage from './features/labs/components/LabCheckoutPage';
import ThankYouPage from './components/ThankYouPage';
import PolicyViewer from './components/PolicyViewer';
import clientConfigService from './services/clientConfig';
import config from './utils/config';
import followUpService, { FollowUpService } from './services/followUp';
import { captureAttributionFromLocation } from './utils/attribution';
import {
  getPreviewBranding,
  getTreatmentLaunchContext,
} from './features/treatment-flow/launchContext';
import {
  QUESTIONNAIRE_LAUNCH_MODE,
  launchRuntimeProps,
  resolveDefaultLegacyLaunch,
  resolveDirectRuntimeLaunch,
  resolvePreviewLaunch,
  resolveVisitLaunch,
} from './features/treatment-flow/launchResolver';
import { consumePatientPortalHandoff } from './features/treatment-flow/authentication/patientPortalHandoff';
import './styles/zyvgo-design.css';
import './styles/questionnaire.css';

function App() {
  const { isAuthenticated, authenticateForQuestionnaire, loading } = useAuth();
  const [authError, setAuthError] = useState(null);
  const [authLoading, setAuthLoading] = useState(false);
  const [appConfig, setAppConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState(null);

  const [clientId, setClientId] = useState(null);
  const [visitType, setVisitType] = useState(null);
  const [questionnaireLaunch, setQuestionnaireLaunch] = useState(null);
  const [visitConfig, setVisitConfig] = useState(null);
  const [couponCode, setCouponCode] = useState(null);
  // Live runtime journeys resolve patient identity from the authenticated
  // runtime session or onboarding. Never send a hard-coded demo ID to the
  // tenant order/checkout APIs.
  const patientId = null;

  // Follow-up specific state
  const [isFollowUp, setIsFollowUp] = useState(false);
  const [followUpAccessBlocked, setFollowUpAccessBlocked] = useState(false);
  const [followUpParams, setFollowUpParams] = useState(null);
  const [followUpVerified, setFollowUpVerified] = useState(false);
  const [followUpPrefillData, setFollowUpPrefillData] = useState(null);
  const [followUpTemplateId, setFollowUpTemplateId] = useState(null);
  const [showConfirmDetails, setShowConfirmDetails] = useState(false);
  const [followUpInitializing, setFollowUpInitializing] = useState(false);
  const [checkoutToken, setCheckoutToken] = useState(null);
  const [labSlug, setLabSlug] = useState(null);
  const [showThankYou, setShowThankYou] = useState(false);
  const initializationSequenceRef = useRef(0);
  const resolvedRuntimeProps = launchRuntimeProps(questionnaireLaunch);
  const {
    templateId: launchTemplateId,
    runtimeProgramId,
    runtimeCustomProgramId,
    previewProgramId,
    previewCustomProgramId,
    previewApiBaseUrl,
    previewCapabilityToken,
    previewSnapshotChecksum,
    previewSnapshotId,
    runtimeApiBaseUrl,
  } = resolvedRuntimeProps;
  // Keep the immutable launch identity as a render-time fallback. This
  // prevents a transient/stale launch-props object from mounting the
  // QuestionnaireForm without its live Program source and silently creating
  // an empty legacy shell.
  const effectiveRuntimeProgramId = runtimeProgramId || (
    questionnaireLaunch?.mode === QUESTIONNAIRE_LAUNCH_MODE.treatmentLive
      && questionnaireLaunch?.sourceKind === "program"
      ? questionnaireLaunch.sourceId
      : null
  );
  const effectiveRuntimeCustomProgramId = runtimeCustomProgramId || (
    questionnaireLaunch?.mode === QUESTIONNAIRE_LAUNCH_MODE.treatmentLive
      && questionnaireLaunch?.sourceKind === "custom_program"
      ? questionnaireLaunch.sourceId
      : null
  );
  const templateId = isFollowUp ? followUpTemplateId : launchTemplateId;

  useVisitorTracking();

  // Initialize configuration from URL
  useEffect(() => {
    initializeFromUrl();
  }, []);

  const initializeFromUrl = async () => {
    const initializationSequence = ++initializationSequenceRef.current;
    const assertCurrentInitialization = () => {
      if (initializationSequence !== initializationSequenceRef.current) {
        const superseded = new Error('Questionnaire initialization was superseded.');
        superseded.code = 'questionnaire_initialization_superseded';
        throw superseded;
      }
    };
    try {
      setConfigLoading(true);
      setConfigError(null);
      setQuestionnaireLaunch(null);

      const pathParts = window.location.pathname.split('/').filter(part => part);
      const hostname = window.location.hostname;
      captureAttributionFromLocation(window.location);

      // Persist promo/referral from URL immediately (e.g. ?promo=AMEER&promo-source=coupon from Coupon modal)
      // so order create has it even if init fails or returns early (checkout/thank-you/follow-up).
      const isCheckout = pathParts.indexOf('checkout') !== -1;
      const isThankYou = pathParts.indexOf('thank-you') !== -1;
      if (!isCheckout && !isThankYou && window.location.search) {
        const q = new URLSearchParams(window.location.search);
        const promo = (q.get('promo') || q.get('coupon') || '').trim();
        const ref = (q.get('referral-campaign') || '').trim();
        const refSource = q.get('promo-source') || (ref ? 'affiliate' : '');
        if (promo) {
          sessionStorage.setItem('welliemd_coupon_code', promo);
          if (ref) {
            sessionStorage.setItem('welliemd_referral_campaign', ref);
            sessionStorage.setItem('welliemd_referral_source', refSource || 'affiliate');
          }
          console.log('🎟️ Promo/referral persisted from URL (for order create):', { promo, referralCampaign: ref || null });
        } else if (ref) {
          sessionStorage.setItem('welliemd_referral_campaign', ref);
          sessionStorage.setItem('welliemd_referral_source', refSource || 'affiliate');
          sessionStorage.setItem('welliemd_coupon_code', ref);
          console.log('🎟️ Referral persisted from URL (for order create):', ref);
        }
      }

      console.log('Initializing app:', { hostname, pathParts });

      // Check if this is a follow-up URL first
      const followUpData = FollowUpService.parseFollowUpUrl();
      if (followUpData) {
        console.log('🔐 Follow-up URL detected:', followUpData);
        setIsFollowUp(true);
        setFollowUpParams(followUpData);

        // Clear any stale template/client endpoint from a previous questionnaire session.
        sessionStorage.removeItem('followup_questionnaire_id');

        // Check if patient is pre-authenticated from Patient Portal (access_token in URL)
        if (followUpData.accessToken) {
          console.log('🔓 Pre-authenticated from Patient Portal - skipping verify');
          // Store the access token and mark as verified
          followUpService.storeAccessTokenFromPortal(
            followUpData.sessionId,
            followUpData.accessToken
          );
          setFollowUpVerified(true);
          // We need to get the questionnaire ID - call a lightweight endpoint or use session info
          // For now, we'll load the questionnaire form after getting client config
        }

        // Still need to load client config for branding
        // IMPORTANT: Pass followUpData directly to avoid race condition with async state
        await loadClientConfigForFollowUp(hostname, pathParts, followUpData);
        return;
      }

      // Check for Checkout Token URL (/checkout/:token)
      const checkoutIndex = pathParts.indexOf('checkout');
      if (checkoutIndex !== -1 && pathParts[checkoutIndex + 1]) {
        const token = pathParts[checkoutIndex + 1];
        console.log('Checkout token detected:', token);
        setCheckoutToken(token);
        // We still need client config for branding
        await loadClientConfigForCheckout(hostname, token);
        return;
      }

      // Check for Lab Storefront URL (/labs/:slug)
      const labsIndex = pathParts.indexOf('labs');
      if (labsIndex !== -1 && pathParts[labsIndex + 1]) {
        const slug = pathParts[labsIndex + 1];
        console.log('Lab storefront detected:', slug);
        setLabSlug(slug);
        await loadClientConfigForCheckout(hostname, slug);
        return;
      }

      // Check for Thank You page (/thank-you or /:clientId/thank-you)
      const thankYouIndex = pathParts.indexOf('thank-you');
      if (thankYouIndex !== -1) {
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        let thankYouClientId = null;
        if (thankYouIndex === 0 && pathParts.length === 1) {
          // Path is just /thank-you. On tenant domains, the domain is authoritative;
          // localStorage can be stale if the browser previously visited another tenant.
          // If domain resolution fails (local/dev/incognito), fall back to checkout storage.
          try {
            const resolvedClient = await clientConfigService.resolveClientByDomain(hostname);
            if (resolvedClient?.id) {
              thankYouClientId = resolvedClient.id;
              console.log('Thank-you page: resolved client from domain:', thankYouClientId);
            }
          } catch (e) {
            console.warn('Thank-you page: could not resolve client from domain:', e?.message);
          }
          if (!thankYouClientId) {
            thankYouClientId = localStorage.getItem('welliemd_client_id');
          }
          if (!thankYouClientId) {
            console.warn('Thank-you page: no client found in domain resolution or storage');
          }
        } else if (thankYouIndex === 1 && pathParts.length === 2 && uuidRegex.test(pathParts[0])) {
          thankYouClientId = pathParts[0];
        }
        if (thankYouClientId) {
          console.log('Thank-you page detected, client:', thankYouClientId);
          setClientId(thankYouClientId);
          setShowThankYou(true);
          const clientConfig = await clientConfigService.getClientConfig(thankYouClientId);
          setAppConfig(clientConfig);
          if (clientConfig?.id) localStorage.setItem('welliemd_client_id', clientConfig.id);
          if (clientConfig?.name) localStorage.setItem('welliemd_client_name', clientConfig.name);
          if (clientConfig?.api_endpoint) localStorage.setItem('welliemd_client_api_endpoint', clientConfig.api_endpoint);
          clientConfigService.applyBranding(clientConfig);
          setConfigLoading(false);
          return;
        }
        // If /thank-you with no client and domain resolution failed, show thank-you with minimal config so we never call questionnaire init (which would fail with wrong template)
        if (thankYouIndex === 0 && pathParts.length === 1) {
          console.log('Thank-you page: showing with minimal config (no client resolved)');
          setShowThankYou(true);
          setAppConfig({ name: 'Support', questionnaire_config: { company_name: 'our support team' } });
          setConfigLoading(false);
          return;
        }
      }

      // 1. Extract Client Slug/ID from URL
      // Format expected: /slug-name/visit/... OR /uuid/visit/...
      // Assume the first part of the path is the client identifier (slug or UUID)
      // e.g. /knysys/visit -> 'knysys'
      const urlParams = new URLSearchParams(window.location.search);
      const treatmentLaunch = getTreatmentLaunchContext(window.location.search);
      const { isAdminPreview, previewProgramId, previewCustomProgramId } = treatmentLaunch;
      let clientIdentifier = pathParts[0];
      let extractedClientId = null;
      let extractedVisitType = null;

      const visitIndex = pathParts.findIndex((part) => part === 'visit' || part === 'start');
      if (visitIndex !== -1 && pathParts[visitIndex + 1]) {
        // Pass as-is so slug URLs (e.g. /visit/glutathione, /start/nad) match backend slug lookup; backend uses slug__iexact
        extractedVisitType = pathParts[visitIndex + 1];
        console.log('Extracted visit type/slug from URL:', extractedVisitType);
      }

      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

      // 2. Fallback to Default Config (Environment Variable) if URL is empty
      if (
        !clientIdentifier ||
        clientIdentifier === 'visit' ||
        clientIdentifier === 'start' ||
        (isAdminPreview && clientIdentifier === 'preview')
      ) {
        console.log("No client found in URL, using default from env:", config.DEFAULT_CLIENT_ID);
        clientIdentifier = config.DEFAULT_CLIENT_ID;
      }

      const previewBranding = getPreviewBranding(treatmentLaunch);
      if (previewBranding) {
        const previewLaunch = resolvePreviewLaunch({
          search: window.location.search,
          hash: window.location.hash,
        });
        const previewClientId = previewBranding.id;
        console.log(
          `Using admin ${previewBranding.name} mode:`,
          previewProgramId || previewCustomProgramId
        );
        setClientId(previewClientId);
        setVisitType(null);
        setQuestionnaireLaunch(previewLaunch);
        setAppConfig({
          id: previewClientId,
          name: previewBranding.name,
          questionnaire_config: {
            company_name: previewBranding.name,
          },
        });
        localStorage.setItem('welliemd_client_id', previewClientId);
        setConfigLoading(false);
        return;
      }

      // Check if clientIdentifier is a UUID
      if (clientIdentifier && uuidRegex.test(clientIdentifier)) {
        extractedClientId = clientIdentifier;
        console.log('Extracted client ID from URL:', extractedClientId);
      }

      // If not found yet, try domain resolution
      if (!extractedClientId) {
        console.log('No client ID in URL, trying domain resolution for:', hostname);
        try {
          const resolvedClient = await clientConfigService.resolveClientByDomain(hostname);
          if (!resolvedClient?.id) {
            throw new Error('Domain resolution did not return a client ID');
          }
          extractedClientId = resolvedClient.id;
          console.log('Client resolved from domain:', resolvedClient);
        } catch (domainError) {
          console.warn('Domain resolution failed:', domainError.message);
          if (domainError.code === 'client_inactive') {
            throw domainError;
          }

          // If we have a clientIdentifier (slug), try to use it directly
          if (clientIdentifier && clientIdentifier !== 'visit') {
            console.log('Trying to use clientIdentifier as slug:', clientIdentifier);
            extractedClientId = clientIdentifier; // Service will handle slug resolution
          } else {
            throw new Error(
              `No client found. Please use one of these formats:\n` +
              `• http://client1.local:3000/visit/GLP (domain + visit type)\n` +
              `• http://localhost:3000/your-client-id/visit/GLP (client ID + visit type)\n` +
              `• http://localhost:3000/your-client-id/ (client ID only)`
            );
          }
        }
      }

      if (!extractedClientId) {
        throw new Error('No valid client ID found in URL or configuration.');
      }

      console.log('🔍 Initializing with Client Identifier:', extractedClientId);

      setClientId(extractedClientId);
      setVisitType(extractedVisitType);

      // 3. Fetch Config using the Identifier (Service handles both UUID and Slug)
      // Domain resolution intentionally returns only identity/basic routing data.
      // Always load the secured configuration before applying branding, API
      // endpoints, tracking, and questionnaire defaults.
      const clientConfig = await clientConfigService.getClientConfig(extractedClientId);
      assertCurrentInitialization();
      if (!clientConfig?.id) {
        throw new Error('Client configuration response is missing client ID.');
      }

      // 4. Update State with the REAL UUID from the response
      const realClientId = clientConfig.id;
      setClientId(realClientId);
      setAppConfig(clientConfig);

      // Store the actual client ID from the database (not the extracted one from URL)
      if (clientConfig.id) {
        localStorage.setItem('welliemd_client_id', clientConfig.id);
        console.log('📍 Client ID stored:', clientConfig.id);
      }

      // Store client name for cross-database compatibility
      if (clientConfig.name) {
        localStorage.setItem('welliemd_client_name', clientConfig.name);
        console.log('📍 Client name stored:', clientConfig.name);
      }

      // Store client API endpoint for questionnaire operations
      if (clientConfig.api_endpoint) {
        localStorage.setItem('welliemd_client_api_endpoint', clientConfig.api_endpoint);
        console.log('📍 Client API endpoint:', clientConfig.api_endpoint);
      }

      // Apply branding immediately
      clientConfigService.applyBranding(clientConfig);

      // Published intake URLs already carry the immutable live runtime source.
      // Resolve that source immediately after client config so a cold launch
      // does not depend on the legacy visit-config request completing first.
      // This is especially important for Patient Portal launches, where the
      // handoff exchange and runtime session must be ready before the form
      // mounts. Legacy /visit URLs continue through visit config below.
      const directRuntimeLaunch = resolveDirectRuntimeLaunch({
        programId: urlParams.get('program_id'),
        customProgramId: urlParams.get('custom_program_id'),
        apiBaseUrl: clientConfig.api_endpoint,
      });

      if (directRuntimeLaunch) {
        setVisitType(null);
        setVisitConfig(null);
        await consumePatientPortalHandoff({
          search: window.location.search,
          apiBaseUrl: directRuntimeLaunch.apiBaseUrl,
        });
        assertCurrentInitialization();
        setQuestionnaireLaunch(directRuntimeLaunch);
        console.log('Using direct live runtime launch from URL:', {
          mode: directRuntimeLaunch.mode,
          sourceKind: directRuntimeLaunch.sourceKind,
          sourceId: directRuntimeLaunch.sourceId,
        });
      } else if (extractedVisitType) {
        console.log('Fetching visit configuration for:', extractedVisitType);
        const vConfig = await clientConfigService.getVisitConfig(realClientId, extractedVisitType);
        assertCurrentInitialization();
        setVisitConfig(vConfig);

        // Use visit config's client so policy links and API use same client and domain as visit
        if (vConfig.client?.id) {
          const visitClientId = vConfig.client.id;
          setClientId(visitClientId);
          localStorage.setItem('welliemd_client_id', visitClientId);
          if (vConfig.client.name) {
            localStorage.setItem('welliemd_client_name', vConfig.client.name);
          }
          setAppConfig((prev) => (prev ? { ...prev, id: visitClientId, name: vConfig.client.name || prev.name, patient_portal_domain: vConfig.client.patient_portal_domain || prev.patient_portal_domain } : prev));
        }

        let resolvedLaunch = resolveVisitLaunch({
          visitConfig: vConfig,
          fallbackTemplateId: clientConfig.default_template_id,
          runtimeApiBaseUrl: clientConfig.api_endpoint,
        });

        // A copied published release URL may include the runtime source
        // identity even when an older visit-config response is legacy-shaped.
        // Prefer that immutable URL identity so the live Program renderer does
        // not fall through to an empty legacy shell.
        if (resolvedLaunch.mode === QUESTIONNAIRE_LAUNCH_MODE.legacy) {
          const directRuntimeLaunch = resolveDirectRuntimeLaunch({
            programId: urlParams.get('program_id'),
            customProgramId: urlParams.get('custom_program_id'),
            templateId: vConfig.runtime?.template_id
              || vConfig.form_template?.id
              || clientConfig.default_template_id,
            apiBaseUrl: clientConfig.api_endpoint,
          });
          if (directRuntimeLaunch) resolvedLaunch = directRuntimeLaunch;
        }
        if (
          resolvedLaunch.mode === QUESTIONNAIRE_LAUNCH_MODE.legacy
          && resolvedLaunch.questionnaireType === 'follow_up'
        ) {
          console.error('❌ Follow-up questionnaire cannot be accessed via /visit/ route');
          setFollowUpAccessBlocked(true);
          setConfigLoading(false);
          return;
        }
        if (resolvedLaunch.mode === QUESTIONNAIRE_LAUNCH_MODE.treatmentLive) {
          await consumePatientPortalHandoff({
            search: window.location.search,
            apiBaseUrl: resolvedLaunch.apiBaseUrl,
          });
          assertCurrentInitialization();
        }
        setQuestionnaireLaunch(resolvedLaunch);
        console.log('Using resolved questionnaire launch:', {
          mode: resolvedLaunch.mode,
          sourceKind: resolvedLaunch.sourceKind,
          sourceId: resolvedLaunch.sourceId,
          templateId: resolvedLaunch.templateId,
        });
      } else {
        // Set template ID from config or URL params
        const configTemplateId = clientConfig.default_template_id || null;
        const finalTemplateId = urlParams.get('template_id') || configTemplateId;
        const resolvedLaunch = resolveDefaultLegacyLaunch(finalTemplateId);
        setQuestionnaireLaunch(resolvedLaunch);
        console.log('No visit type, using legacy template:', finalTemplateId);
      }

      // Read coupon/referral from URL (e.g. from Coupon Codes modal: ?promo=AMEER&promo-source=coupon)
      const urlCouponCode = urlParams.get('coupon') || urlParams.get('promo');
      const referralCampaign = urlParams.get('referral-campaign');
      const promoSource = urlParams.get('promo-source') || (referralCampaign ? 'affiliate' : '');

      if (urlCouponCode) {
        setCouponCode(urlCouponCode);
        sessionStorage.setItem('welliemd_coupon_code', urlCouponCode.trim());
        if (referralCampaign) {
          sessionStorage.setItem('welliemd_referral_campaign', referralCampaign);
          sessionStorage.setItem('welliemd_referral_source', promoSource || 'affiliate');
        }
        console.log('🎟️ Coupon code from URL:', urlCouponCode);
      } else if (referralCampaign) {
        // Affiliate referral - lookup affiliate's coupon
        sessionStorage.setItem('welliemd_referral_campaign', referralCampaign);
        sessionStorage.setItem('welliemd_referral_source', promoSource || 'affiliate');
        console.log('🔗 Referral campaign from URL:', referralCampaign);
        try {
          const clientApiEndpoint = localStorage.getItem('welliemd_client_api_endpoint');
          if (clientApiEndpoint) {
            const response = await fetch(`${clientApiEndpoint}/api/v1/affiliates/by-slug/${referralCampaign}/coupon/`);
            if (response.ok) {
              const data = await response.json();
              if (data.coupon_code) {
                setCouponCode(data.coupon_code);
                sessionStorage.setItem('welliemd_coupon_code', data.coupon_code.trim());
                console.log('🎟️ Affiliate coupon auto-applied:', data.coupon_code);
              } else {
                sessionStorage.setItem('welliemd_coupon_code', referralCampaign.trim());
              }
            } else {
              sessionStorage.setItem('welliemd_coupon_code', referralCampaign.trim());
            }
          } else {
            sessionStorage.setItem('welliemd_coupon_code', referralCampaign.trim());
          }
        } catch (e) {
          console.warn('Could not lookup affiliate coupon:', e.message);
          sessionStorage.setItem('welliemd_coupon_code', referralCampaign.trim());
        }
      }

      console.log('✅ App initialized with client config:', {
        clientId: realClientId,
        clientName: clientConfig.name,
        visitType: extractedVisitType,
        launchMode: questionnaireLaunch?.mode,
        hasVisitConfig: !!visitConfig
      });

    } catch (error) {
      if (error.code === 'questionnaire_initialization_superseded') return;
      console.error('❌ Failed to initialize app:', error);
      setConfigError({
        message: error.message,
        code: error.code,
        brandName: error.brandName
      });
    } finally {
      if (initializationSequence === initializationSequenceRef.current) {
        setConfigLoading(false);
      }
    }
  };

  // Load client config for follow-up flow (without visit type requirements)
  // IMPORTANT: followUpData is passed directly to avoid React state race condition
  const loadClientConfigForFollowUp = async (hostname, pathParts, followUpData = null) => {
    let extractedClientId = followUpData?.clientId || null;

    if (followUpData?.apiBaseUrl) {
      localStorage.setItem('welliemd_client_api_endpoint', followUpData.apiBaseUrl);
    }

    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    if (!extractedClientId) {
      for (const part of pathParts) {
        if (uuidRegex.test(part) && part !== followUpData?.sessionId) {
          extractedClientId = part;
          break;
        }
      }
    }

    const hydrateClientBranding = async () => {
      let resolvedClientId = extractedClientId;

      if (!resolvedClientId) {
        try {
          const resolvedClient = await clientConfigService.resolveClientByDomain(hostname);
          resolvedClientId = resolvedClient.id;
        } catch (e) {
          if (e.code === 'client_inactive') {
            throw e;
          }
          console.warn('Could not resolve client for follow-up branding, continuing without it');
          return;
        }
      }

      if (!resolvedClientId) {
        return;
      }

      try {
        setClientId(resolvedClientId);
        const clientConfig = await clientConfigService.getClientConfig(resolvedClientId);
        setAppConfig(clientConfig);

        if (clientConfig.api_endpoint) {
          localStorage.setItem('welliemd_client_api_endpoint', clientConfig.api_endpoint);
        }

        clientConfigService.applyBranding(clientConfig);
      } catch (error) {
        if (error.code === 'client_inactive') {
          throw error;
        }
        console.warn('Failed to load client config for follow-up branding:', error);
      }
    };

    try {
      // For portal-authenticated follow-ups, start the questionnaire first so
      // branding/config fetches can never leave the app stuck on the loading view.
      if (followUpData?.accessToken && followUpData?.sessionId) {
        setFollowUpInitializing(true);

        // Load branding in the background. The questionnaire runtime can proceed
        // using the tenant API endpoint from the follow-up URL itself.
        void hydrateClientBranding();

        try {
          console.log('Fetching follow-up start payload for pre-authenticated flow...');
          const startResult = await followUpService.startQuestionnaire();
          const questionnaireId = startResult.questionnaire_id || sessionStorage.getItem('followup_questionnaire_id');

          if (!questionnaireId) {
            throw new Error('Follow-up questionnaire ID was not returned by the server.');
          }

          setFollowUpTemplateId(questionnaireId);

          const normalizedPrefill = startResult.prefill_data
            ? {
                ...(startResult.prefill_data || {}),
                patient: startResult.patient || null
              }
            : null;

          if (normalizedPrefill) {
            console.log('Prefill data retrieved successfully:', startResult.prefill_data);
          }

          handleFollowUpVerified({
            questionnaireId,
            prefillData: normalizedPrefill,
            sessionId: followUpData.sessionId
          });
        } catch (e) {
          console.error('Could not initialize pre-authenticated session:', e);
          setConfigError(e?.message || 'Failed to initialize follow-up questionnaire.');
        } finally {
          setFollowUpInitializing(false);
        }

        return;
      }

      await hydrateClientBranding();
    } catch (error) {
      console.warn('Failed to load client config for follow-up:', error);
      if (error.code === 'client_inactive') {
        setIsFollowUp(false);
        setConfigError({
          message: error.message,
          code: error.code,
          brandName: error.brandName
        });
      }
      // Continue anyway - follow-up should work based on session
    } finally {
      setConfigLoading(false);
    }
  };

  // Load client config for checkout flow (minimal config needed for branding)
  const loadClientConfigForCheckout = async (hostname, token) => {
    try {
      // The backend routes combined recovery tokens on shared local hosts.
      // Tenant domains continue to resolve their own configuration.
      const normalizedHostname = String(hostname || '').toLowerCase();
      const isLocalQuestionnaireHost = (
        normalizedHostname === 'localhost'
        || normalizedHostname.endsWith('.localhost')
        || ['127.0.0.1', '::1', '[::1]'].includes(normalizedHostname)
      );
      let extractedClientId = isLocalQuestionnaireHost
        ? new URLSearchParams(window.location.search).get('client_id')
        : null;
      let resolvedByCheckoutToken = false;
      if (isLocalQuestionnaireHost && token && !token.startsWith('v1.')
        && new URLSearchParams(window.location.search).get('flow') === 'combined') {
        try {
          const resolved = await clientConfigService.resolveClientByCheckoutToken(token);
          extractedClientId = resolved.id;
          resolvedByCheckoutToken = true;
        } catch (error) {
          if (error.code === 'client_inactive') throw error;
          // Keep existing links usable while they still contain a tenant hint.
          if (!extractedClientId) {
            error.code = 'checkout_tenant_unavailable';
            throw error;
          }
        }
      }

      if (!extractedClientId) {
        try {
          const resolvedClient = await clientConfigService.resolveClientByDomain(hostname);
          extractedClientId = resolvedClient.id;
        } catch (e) {
          if (e.code === 'client_inactive') {
            throw e;
          }
          console.warn('Could not resolve client by domain for checkout, attempting fallbacks');
        }
      }

      if (!extractedClientId) {
        extractedClientId = localStorage.getItem('welliemd_client_id');
      }

      if (!extractedClientId && token) {
        try {
          const storedCheckout = sessionStorage.getItem(treatmentCheckoutStorageKey(token))
            || sessionStorage.getItem(`welliemd_phase2_checkout:${token}`);
          if (storedCheckout) {
            const parsed = JSON.parse(storedCheckout);
            extractedClientId = parsed?.client_id;
          }
        } catch (_) {}
      }

      if (extractedClientId) {
        setClientId(extractedClientId);
        const clientConfig = await clientConfigService.getClientConfig(extractedClientId);
        setAppConfig(clientConfig);

        if (clientConfig.id) {
          localStorage.setItem('welliemd_client_id', clientConfig.id);
        }
        if (clientConfig.name) {
          localStorage.setItem('welliemd_client_name', clientConfig.name);
        }
        if (clientConfig.api_endpoint) {
          localStorage.setItem('welliemd_client_api_endpoint', clientConfig.api_endpoint);
        }

        clientConfigService.applyBranding(clientConfig);
        if (resolvedByCheckoutToken) {
          const cleanUrl = new URL(window.location.href);
          cleanUrl.searchParams.delete('client_id');
          window.history.replaceState(window.history.state, '', cleanUrl.href);
        }
      }

    } catch (error) {
      console.warn('Failed to load client config for checkout:', error);
      if (error.code === 'checkout_tenant_unavailable') {
        setCheckoutToken(null);
        setConfigError('This checkout link is unavailable. Please retry or restart the questionnaire.');
      }
      if (error.code === 'client_inactive') {
        setCheckoutToken(null);
        setConfigError({
          message: error.message,
          code: error.code,
          brandName: error.brandName
        });
      }
      // Continue anyway - checkout should work without branding
    } finally {
      setConfigLoading(false);
    }
  };


  // Handle follow-up verification success
  const handleFollowUpVerified = ({ questionnaireId, prefillData, sessionId }) => {
    console.log('✅ Follow-up verified:', { questionnaireId, prefillData });
    setFollowUpVerified(true);
    setFollowUpTemplateId(questionnaireId);

    if (prefillData) {
      setFollowUpPrefillData(prefillData);
      setShowConfirmDetails(true);
    }
  };

  // Handle confirm details
  const handleConfirmDetails = (confirmedData) => {
    console.log('✅ Details confirmed:', confirmedData);
    setShowConfirmDetails(false);
    // Proceed to questionnaire with prefill
  };

  // Handle edit details
  const handleEditDetails = () => {
    setShowConfirmDetails(false);
    // DO NOT clear prefill so form shows with existing values for editing
  };

  const handleAutoAuth = async () => {
    if (!isAuthenticated && !loading && templateId && clientId) {
      setAuthLoading(true);
      setAuthError(null);

      try {
        const result = await authenticateForQuestionnaire(templateId);

        if (!result.success) {
          setAuthError(result.error);
        }
      } catch (error) {
        setAuthError(error.message);
      } finally {
        setAuthLoading(false);
      }
    }
  };

  const handleQuestionnaireComplete = (data) => {
    console.log('Questionnaire completed:', data);

    // Clear follow-up context on completion
    if (isFollowUp) {
      followUpService.clearContext();
    }

    const companyName = appConfig?.questionnaire_config?.company_name || appConfig?.name || 'WellieMD';
    const visitTypeText = visitType ? ` ${visitType}` : '';
  };

  // Policy viewer: clean URL /policy/<policy_type> (e.g. /policy/terms_of_service)
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const policyPathMatch = pathname.match(/^\/policy\/(terms_of_service|privacy_policy|consent_telehealth)\/?$/i);
  if (policyPathMatch) {
    return (
      <PolicyViewer
        policyType={policyPathMatch[1].toLowerCase()}
      />
    );
  }

  // Thank-you page (after checkout success)
  if (showThankYou) {
    if (configLoading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <LoadingSpinner text="Loading..." />
        </div>
      );
    }
    return <ThankYouPage clientConfig={appConfig} />;
  }

  // Checkout flow rendering
  if (checkoutToken) {
    if (configLoading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <LoadingSpinner text="Loading checkout..." />
        </div>
      );
    }
    const isPhase2Checkout = Boolean(
      checkoutToken.startsWith('v1.')
      || new URLSearchParams(window.location.search).get('flow') === 'combined'
      || sessionStorage.getItem(treatmentCheckoutStorageKey(checkoutToken)),
    );
    return isPhase2Checkout
      ? <TreatmentCheckoutPage token={checkoutToken} clientConfig={appConfig} />
      : <CheckoutPage token={checkoutToken} clientConfig={appConfig} />;
  }

  // Lab storefront rendering
  if (labSlug) {
    if (configLoading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <LoadingSpinner text="Preparing checkout..." />
        </div>
      );
    }
    return <LabCheckoutPage slug={labSlug} clientConfig={appConfig} />;
  }

  // Follow-up flow rendering
  if (isFollowUp) {
    // Still loading client config
    if (configLoading || followUpInitializing) {
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          background: 'var(--zyvgo-background, #FAFAFA)'
        }}>
          <LoadingSpinner text="Loading..." />
        </div>
      );
    }

    if (configError) {
      return (
        <div style={{
          padding: '40px',
          textAlign: 'center',
          maxWidth: '700px',
          margin: '0 auto',
          background: 'var(--zyvgo-background, #FAFAFA)',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}>
          <h2 style={{ color: '#dc3545' }}>Configuration Error</h2>
          <p style={{ color: '#dc3545', marginBottom: '20px', whiteSpace: 'pre-line' }}>
            {configError}
          </p>
          <button
            onClick={initializeFromUrl}
            style={{
              padding: '12px 24px',
              backgroundColor: 'var(--zyvgo-primary, #222222)',
              color: 'white',
              border: 'none',
              borderRadius: '24px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '1rem'
            }}
          >
            Retry Follow-Up
          </button>
        </div>
      );
    }

    // Not yet verified - show access gate
    if (!followUpVerified) {
      return (
        <FollowUpAccessGate
          sessionId={followUpParams?.sessionId}
          token={followUpParams?.token}
          onVerified={handleFollowUpVerified}
          clientConfig={appConfig}
        />
      );
    }

    // Show confirm details if we have prefill data
    if (showConfirmDetails && followUpPrefillData) {
      return (
        <ConfirmDetails
          prefillData={followUpPrefillData}
          onConfirm={handleConfirmDetails}
          onEdit={handleEditDetails}
          clientConfig={appConfig}
        />
      );
    }

    // Verified - show questionnaire
    if (templateId) {
      return (
        <div className="App">
          <QuestionnaireForm
            templateId={templateId}
            patientId={patientId}
            clientConfig={appConfig}
            visitConfig={visitConfig}
            visitType={visitType}
            onComplete={handleQuestionnaireComplete}
            prefillData={followUpPrefillData}
            isFollowUp={true}
            followUpAccessToken={followUpService.getAccessToken()}
            couponCode={couponCode}
          />
        </div>
      );
    }

    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'var(--zyvgo-background, #FAFAFA)'
      }}>
        <LoadingSpinner text="Preparing your follow-up..." />
      </div>
    );
  }

  // 🚫 Follow-up accessed via /visit/ URL - BLOCKED
  if (followUpAccessBlocked) {
    // Get patient portal domain from visit config (loaded from Client table)
    const portalDomain = visitConfig?.client?.patient_portal_domain || appConfig?.patient_portal_domain || '';
    const patientPortalUrl = portalDomain ? `${portalDomain.replace(/\/$/, '')}/auth/signin` : '/auth/signin';

    return (
      <div style={{
        padding: '40px',
        textAlign: 'center',
        maxWidth: '600px',
        margin: '0 auto',
        background: 'var(--zyvgo-background, #FAFAFA)',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <div style={{
          backgroundColor: '#FEF2F2',
          border: '1px solid #FECACA',
          borderRadius: '12px',
          padding: '32px',
          maxWidth: '450px'
        }}>
          <div style={{
            fontSize: '48px',
            marginBottom: '16px'
          }}>🔒</div>
          <h2 style={{
            color: '#DC2626',
            marginBottom: '16px',
            fontSize: '1.5rem'
          }}>
            Follow-Up Access Required
          </h2>
          <p style={{
            color: '#7F1D1D',
            marginBottom: '24px',
            lineHeight: '1.6'
          }}>
            This questionnaire requires a secure access link that is sent to your email or available in your Patient Portal.
          </p>
          <p style={{
            color: '#991B1B',
            marginBottom: '24px',
            fontSize: '0.9rem'
          }}>
            Please log into your Patient Portal to access your follow-up questionnaire.
          </p>
          <button
            onClick={() => window.location.href = patientPortalUrl}
            style={{
              padding: '14px 28px',
              backgroundColor: 'var(--zyvgo-primary, #222222)',
              color: 'white',
              border: 'none',
              borderRadius: '24px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '1rem',
              transition: 'transform 0.2s, box-shadow 0.2s'
            }}
            onMouseOver={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
            }}
            onMouseOut={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = 'none';
            }}
          >
            Go to Patient Portal
          </button>
        </div>
      </div>
    );
  }

  // Normal onboarding flow
  if (configLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'var(--zyvgo-background, #FAFAFA)'
      }}>
        <LoadingSpinner text="Loading configuration..." />
      </div>
    );
  }

  if (configError) {
    if (configError.code === 'client_inactive') {
      const brandName = configError.brandName || appConfig?.name || 'Brand';
      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          background: 'var(--zyvgo-background, #FAFAFA)'
        }}>
          <div style={{
            width: '100%',
            maxWidth: '560px',
            padding: '40px',
            textAlign: 'center',
            background: '#FFFFFF',
            border: '1px solid #E5E7EB',
            borderRadius: '16px',
            boxShadow: '0 18px 45px rgba(15, 23, 42, 0.08)'
          }}>
            <h1 style={{
              margin: '0 0 14px',
              color: 'var(--zyvgo-text-primary, #222222)',
              fontSize: '1.75rem'
            }}>
              Intake temporarily unavailable
            </h1>
            <p style={{
              margin: 0,
              color: 'var(--zyvgo-text-secondary, #666666)',
              lineHeight: 1.7
            }}>
              This intake is temporarily unavailable. Please contact {brandName} support.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div style={{
        padding: '40px',
        textAlign: 'center',
        maxWidth: '700px',
        margin: '0 auto',
        background: 'var(--zyvgo-background, #FAFAFA)',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
      }}>
        <h2 style={{ color: '#dc3545' }}>Configuration Error</h2>
        <p style={{ color: '#dc3545', marginBottom: '20px', whiteSpace: 'pre-line' }}>
          {configError.message || configError}
        </p>
        <p style={{ color: '#6c757d', fontSize: '14px', marginBottom: '20px' }}>
          Make sure your URL includes a valid client ID:<br />
          <code>http://localhost:3001/your-client-id/</code>
        </p>
        <button
          onClick={() => {
            clientConfigService.clearCache();
            initializeFromUrl();
          }}
          style={{
            padding: '12px 24px',
            backgroundColor: 'var(--zyvgo-primary, #222222)',
            color: 'white',
            border: 'none',
            borderRadius: '24px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '1rem'
          }}
        >
          Retry Configuration
        </button>
      </div>
    );
  }

  if (loading || authLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'var(--zyvgo-background, #FAFAFA)'
      }}>
        <LoadingSpinner text="Initializing secure questionnaire..." />
      </div>
    );
  }

  if (authError) {
    return (
      <div style={{
        padding: '40px',
        textAlign: 'center',
        maxWidth: '600px',
        margin: '0 auto',
        background: 'var(--zyvgo-background, #FAFAFA)',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
      }}>
        <h2>Authentication Error</h2>
        <p style={{ color: '#dc3545', marginBottom: '20px' }}>{authError}</p>
        <button
          onClick={handleAutoAuth}
          style={{
            padding: '12px 24px',
            backgroundColor: 'var(--zyvgo-primary, #222222)',
            color: 'white',
            border: 'none',
            borderRadius: '24px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '1rem'
          }}
        >
          Retry Authentication
        </button>
      </div>
    );
  }

  return (
    <div className="App">
      {(
        templateId
        || effectiveRuntimeProgramId
        || effectiveRuntimeCustomProgramId
        || previewProgramId
        || previewCustomProgramId
      ) && clientId ? (
        <QuestionnaireForm
          templateId={templateId}
          patientId={patientId}
          clientConfig={appConfig}
          visitConfig={visitConfig}
          visitType={visitType}
          onComplete={handleQuestionnaireComplete}
          couponCode={couponCode}
          previewProgramId={previewProgramId}
          previewApiBaseUrl={previewApiBaseUrl}
          runtimeProgramId={effectiveRuntimeProgramId}
          runtimeCustomProgramId={effectiveRuntimeCustomProgramId}
          previewCustomProgramId={previewCustomProgramId}
          previewCapabilityToken={previewCapabilityToken}
          previewSnapshotChecksum={previewSnapshotChecksum}
          previewSnapshotId={previewSnapshotId}
          runtimeApiBaseUrl={runtimeApiBaseUrl}
        />
      ) : (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          background: 'var(--zyvgo-background, #FAFAFA)'
        }}>
          <LoadingSpinner text="Establishing secure connection..." />
        </div>
      )}
    </div>
  );
}

export default App;
